import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { useBusinessInfo } from '../lib/businessInfo';
import { ArrowRight, ChevronDown, Menu, X, LogOut, Mail, Info, LayoutDashboard, FileText } from 'lucide-react';
import { BOOK_PATH } from '../lib/routes';
import OurStandards from './OurStandards';

const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";
const HERO_BG = "https://customer-assets-wrfwihn1.emergentagent.net/job_65add0f0-f285-46f0-81a0-3fba94192f02/artifacts/lpeydn34_ChatGPT%20Image%20Aug%2018%2C%202026%2C%2011_20_42%20AM.png";
const CABIN_BG = "https://customer-assets-wrfwihn1.emergentagent.net/job_65add0f0-f285-46f0-81a0-3fba94192f02/artifacts/612fplyk_ChatGPT%20Image%20Aug%2019%2C%202026%2C%2002_09_25%20PM.png";
const SKYLINE_BG = "https://customer-assets-wrfwihn1.emergentagent.net/job_65add0f0-f285-46f0-81a0-3fba94192f02/artifacts/uet8eh50_ChatGPT%20Image%20Aug%2019%2C%202026%2C%2009_04_40%20PM.png";
const MILES_BG = "https://customer-assets-wrfwihn1.emergentagent.net/job_65add0f0-f285-46f0-81a0-3fba94192f02/artifacts/10ss7g1a_Buraq%20Miles%20membership%20card%20restored.png";

const Landing = () => {
  const navigate = useNavigate();
  const biz = useBusinessInfo();
  const { currentUser, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const goReserve = () => navigate(currentUser ? BOOK_PATH : '/signup');
  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/');
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white">
      {/* First screen — full viewport hero */}
      <div className="relative min-h-screen flex flex-col">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{ backgroundImage: `url("${HERO_BG}")`, backgroundPosition: '62% center' }}
        />
        {/* Left-side tint for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />

        {/* Header */}
        <header className="relative z-20 w-full py-6 px-6 lg:px-10">
        <div className="w-full flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img src={LOGO} alt="RideBuraq" className="w-10 h-10 object-contain" />
            <h1 className="text-2xl font-bold tracking-wide">
              Ride<span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">Buraq</span>
            </h1>
          </div>
          {currentUser ? (
            <div className="relative flex items-center gap-2" ref={menuRef}>
              <Button
                data-testid="header-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg rounded-full px-5"
              >
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <button
                data-testid="header-menu-btn"
                onClick={() => setMenuOpen((v) => !v)}
                className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 active:scale-95 transition-all"
                aria-label="Menu"
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>

              {menuOpen && (
                  <div className="absolute right-0 top-14 w-52 rounded-2xl border border-white/10 bg-black/95 backdrop-blur-xl shadow-2xl overflow-hidden z-50">
                    <button
                      data-testid="menu-about-btn"
                      onClick={() => { setMenuOpen(false); navigate('/about'); }}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-gray-200 hover:bg-white/5 transition-colors"
                    >
                      <Info className="h-4 w-4 text-orange-400" /> About Us
                    </button>
                    <button
                      data-testid="menu-contact-btn"
                      onClick={() => { setMenuOpen(false); navigate('/contact'); }}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-gray-200 hover:bg-white/5 transition-colors border-t border-white/5"
                    >
                      <Mail className="h-4 w-4 text-orange-400" /> Contact Us
                    </button>
                    <button
                      data-testid="menu-terms-btn"
                      onClick={() => { setMenuOpen(false); navigate('/terms'); }}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-gray-200 hover:bg-white/5 transition-colors border-t border-white/5"
                    >
                      <FileText className="h-4 w-4 text-orange-400" /> Terms & Legal
                    </button>
                    <button
                      data-testid="menu-signout-btn"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors border-t border-white/5"
                    >
                      <LogOut className="h-4 w-4" /> Sign Out
                    </button>
                  </div>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Button
                data-testid="header-signup-btn"
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg rounded-full px-6"
              >
                Get Started
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 w-full px-6 lg:px-10 flex-1 flex items-center">
        <div className="max-w-xl text-left -mt-16 lg:-mt-28">
          <h1
            className="text-5xl md:text-7xl font-semibold leading-[1.08] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            New York,
            <br />
            <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent italic">
              Precisely Driven.
            </span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-md mb-8 leading-relaxed">
            Private chauffeur service for those who value time, discretion and effortless arrival.
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <Button
              size="lg"
              data-testid="hero-book-btn"
              onClick={goReserve}
              className="group bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-orange-500/20 transition-all duration-300"
            >
              Reserve a Journey
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </section>

        {/* Scroll hint */}
        <button
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          className="relative z-10 pb-8 flex flex-col items-center gap-1.5 text-gray-300 hover:text-orange-400 transition-colors mx-auto animate-bounce"
          aria-label="Scroll down"
        >
          <span className="text-[10px] tracking-[0.25em] uppercase">Discover More</span>
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      {/* Your Private Space — cabin experience */}
      <section
        data-testid="private-space-section"
        className="relative w-full overflow-hidden border-t border-white/5"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${CABIN_BG}")`, backgroundPosition: 'right center' }}
        />
        {/* Legibility scrims — strong on the left where copy sits */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 md:via-black/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent md:hidden" />

        <div className="relative z-10 w-full px-6 md:px-12 lg:px-16 py-20 md:py-28 lg:py-32 flex">
          <div className="max-w-xl">
            <p className="text-[11px] md:text-xs tracking-[0.35em] uppercase text-orange-400 mb-5">
              Your Private Space
            </p>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-white mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              The world outside
              <br />
              can wait.
            </h2>
            <p className="text-base md:text-lg text-gray-300/90 leading-relaxed max-w-md mb-10">
              Step in and exhale. Every RideBuraq cabin is a calm, considered retreat —
              arranged around the way you actually like to travel.
            </p>

            <div className="mt-2 border-t border-white/10 pt-8">
              <p
                className="text-2xl md:text-3xl text-white leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                We don’t list amenities.
              </p>
              <p
                className="text-2xl md:text-3xl text-orange-400 leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                We don’t need to.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Brand statement / standards — three columns, numbered */}
      <section className="w-full px-6 py-20 md:py-24 border-t border-gray-800 bg-black/40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14">
            <p className="text-xs tracking-[0.35em] uppercase text-orange-400 mb-4">The RideBuraq Standard</p>
            <h2 className="text-3xl md:text-4xl font-medium text-white max-w-2xl leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Luxury is in the details we never compromise
            </h2>
          </div>

          <div className="flex md:grid md:grid-cols-3 gap-6 md:gap-10 lg:gap-14 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0 pb-5 md:pb-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {[
              {
                kicker: 'A Royal Fleet',
                title: 'The Fleet',
                body: 'Every vehicle is immaculate before you ever see it. Not maintained for approval — maintained for people who would simply never say anything if it weren’t.',
              },
              {
                kicker: 'Trusted & Discreet',
                title: 'Discretion, Assumed',
                body: 'Our chauffeurs are vetted, trained, and quiet by design. What happens in the cabin stays there — we don’t consider this a feature. We consider it the baseline.',
              },
              {
                kicker: 'Effortless Booking',
                title: 'Reserved, Not Requested',
                body: 'No calls. No forms. No confirming twice. When you book, it’s already handled — the way it should be for people who don’t ask twice.',
              },
            ].map((item, i) => (
              <div key={item.title} data-testid={`standard-${i}`} className="group text-left snap-start shrink-0 w-[80%] sm:w-[55%] md:w-auto">
                <span
                  className="block text-3xl md:text-4xl leading-none text-orange-500 mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  0{i + 1}
                </span>
                <p className="text-[11px] tracking-[0.25em] uppercase text-gray-400 mb-3">
                  {item.kicker}
                </p>
                <h3
                  className="text-2xl md:text-[1.6rem] font-medium mb-3 text-white leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm md:text-base leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Standards — what riders can expect */}
      <OurStandards />

      {/* Buraq Miles — loyalty */}
      <section
        data-testid="buraq-miles-section"
        className="relative w-full overflow-hidden border-t border-white/5"
      >
        <div
          className="absolute inset-0 bg-cover"
          style={{ backgroundImage: `url("${MILES_BG}")`, backgroundPosition: 'center right' }}
        />
        {/* Legibility scrims — strong on the left where copy sits */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 md:via-black/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent md:hidden" />

        <div className="relative z-10 w-full px-6 md:px-12 lg:px-16 py-20 md:py-28 lg:py-32 flex">
          <div className="max-w-xl">
            <p className="text-[11px] md:text-xs tracking-[0.35em] uppercase text-orange-400 mb-5">
              Buraq Miles
            </p>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-white mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              The more you arrive,
              <br />
              the more the city opens.
            </h2>
            <p className="text-base md:text-lg text-gray-300/90 leading-relaxed max-w-md mb-8">
              Buraq Miles is our quiet way of keeping good company. The more you travel with us,
              the more the city bends in your favor: priority when it’s busiest, upgrades when the
              moment allows, and a standing that deepens with every journey.
            </p>

            <div className="flex items-center gap-4 text-[11px] md:text-xs tracking-[0.25em] uppercase text-gray-400">
              <span className="text-white">Member</span>
              <span className="text-orange-500">•</span>
              <span className="text-white">Gold</span>
              <span className="text-orange-500">•</span>
              <span className="text-white">Platinum</span>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative w-full overflow-hidden min-h-[420px] md:min-h-[500px] flex items-end">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${SKYLINE_BG}")` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/15 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
        <div className="relative z-10 max-w-3xl px-6 md:px-12 lg:px-16 pb-10 md:pb-14 [text-shadow:0_2px_20px_rgba(0,0,0,0.85)]">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            This Is How You Were Meant to Arrive
          </h2>
          <p className="text-base md:text-lg text-orange-400 tracking-wide mt-2">
            RideBuraq.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full py-10 px-6 bg-black border-t border-gray-800">
        <div className="max-w-6xl mx-auto flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <img src={LOGO} alt="RideBuraq" className="w-7 h-7 object-contain" />
              <span className="text-lg font-semibold">RideBuraq</span>
            </div>
            <div className="flex items-center gap-5 text-sm text-gray-400">
              <button data-testid="footer-about-btn" onClick={() => navigate('/about')} className="hover:text-white transition-colors">About</button>
              <button data-testid="footer-contact-btn" onClick={() => navigate('/contact')} className="hover:text-white transition-colors">Contact</button>
              <button data-testid="footer-chauffeurs-btn" onClick={() => navigate('/drive')} className="hover:text-white transition-colors">Chauffeurs</button>
              <button data-testid="footer-terms-btn" onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms & Legal</button>
            </div>
          </div>
          <p className="text-gray-600 text-xs leading-relaxed max-w-3xl">
            RideBuraq is a technology platform that connects riders with independent, third-party
            transportation providers. RideBuraq does not provide transportation services and is not a
            transportation carrier. All drivers are independent contractors who operate their own
            vehicles and maintain their own insurance and licensing.
          </p>
          <p className="text-gray-600 text-xs" data-testid="footer-business">© 2026 {biz.brand} · {biz.address} · Premium private transportation</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
